import React, { useEffect, useId, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import {
    PREVIEW_LOCK_NOTE_ID,
    usePreviewLock,
} from '@/shared/hooks/usePreviewLock';
import { Paragraphs } from './CandidateAnswers';
import {
    END_REASON_TEXT_MAX,
    REVIEWER_END_REASONS,
    STUDENT_END_REASONS,
    endReasonWords,
    givenByWords,
    sideOfRole,
} from './endReasons';
import { formatDate } from './reviewerStages';

const CODES = { student: STUDENT_END_REASONS, reviewer: REVIEWER_END_REASONS };

const PRIVACY_NOTE = {
    student:
        "Only the program's coordinators see the reason you give. The faculty member does not.",
    reviewer:
        "Only the program's coordinators see the reason. The student does not.",
};

const formatCount = (n) => n.toLocaleString('en-US');

/**
 * Why Confirm cannot be pressed yet, or an empty string. The wording is the
 * text the disabled button is described by.
 */
function problemWith(code, text) {
    if (!code) return 'Choose a reason to continue.';
    if (text.length > END_REASON_TEXT_MAX) {
        return `The details can be at most ${formatCount(END_REASON_TEXT_MAX)} characters.`;
    }
    if (code === 'other' && text.trim() === '') {
        return 'Say why in a few words when the reason is Other.';
    }
    return '';
}

/**
 * Asks for the reason a place or an offer ends. `side` is 'student' (the
 * student leaving or declining) or 'reviewer' (a coordinator or faculty
 * member rescinding or releasing); each lists its own reasons. Confirm hands
 * `{ reasonCode, reasonText }` to `onConfirm`, the text exactly as typed and
 * absent when empty. `refusal` may be text or a node and is shown as an alert.
 * The choice and the text start empty each time the dialog opens.
 */
export default function ReleaseReasonDialog({
    show,
    side,
    title,
    intro,
    confirmLabel,
    busy = false,
    refusal = null,
    onConfirm,
    onHide,
    onExited,
}) {
    const id = useId();
    const locked = usePreviewLock();
    const [code, setCode] = useState('');
    const [text, setText] = useState('');

    useEffect(() => {
        if (show) {
            setCode('');
            setText('');
        }
    }, [show]);

    const problem = problemWith(code, text);
    const blocked = busy || locked || problem !== '';
    const titleId = `${id}-title`;
    const problemId = `${id}-problem`;
    const countId = `${id}-count`;
    const noteId = `${id}-privacy`;
    const describedBy =
        [problem ? problemId : null, locked ? PREVIEW_LOCK_NOTE_ID : null]
            .filter(Boolean)
            .join(' ') || undefined;

    const confirm = () => {
        if (blocked) return;
        onConfirm({
            reasonCode: code,
            ...(text !== '' && { reasonText: text }),
        });
    };

    return (
        <Modal
            show={show}
            onHide={() => {
                if (!busy) onHide();
            }}
            onExited={onExited}
            animation={false}
            restoreFocus
            aria-labelledby={titleId}
        >
            <Modal.Header closeButton={!busy}>
                <Modal.Title as="h2" className="h5" id={titleId}>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {intro && <p>{intro}</p>}
                <fieldset className="mb-3" aria-describedby={noteId}>
                    <legend className="h6">Reason</legend>
                    {CODES[side].map((value) => (
                        <Form.Check
                            key={value}
                            type="radio"
                            id={`${id}-${value}`}
                            name={`${id}-reason`}
                            label={endReasonWords(value, side)}
                            value={value}
                            checked={code === value}
                            onChange={() => setCode(value)}
                        />
                    ))}
                </fieldset>
                <Form.Group controlId={`${id}-details`}>
                    <Form.Label>
                        {code === 'other'
                            ? 'Details (required for Other)'
                            : 'Details (optional)'}
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        aria-required={code === 'other'}
                        aria-describedby={countId}
                    />
                    <Form.Text id={countId} as="div" muted>
                        {`${formatCount(text.length)} of ${formatCount(END_REASON_TEXT_MAX)} characters`}
                    </Form.Text>
                </Form.Group>
                <p id={noteId} className="small text-body-secondary mt-3 mb-0">
                    {PRIVACY_NOTE[side]}
                </p>
                {refusal && (
                    <Alert variant="danger" role="alert" className="mt-3 mb-0">
                        {refusal}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                {problem && !busy && (
                    <span
                        id={problemId}
                        className="small text-body-secondary me-auto"
                    >
                        {problem}
                    </span>
                )}
                <PreviewLockNote locked={locked} />
                <Button
                    variant="outline-primary"
                    onClick={onHide}
                    disabled={busy}
                >
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={confirm}
                    disabled={blocked}
                    aria-describedby={describedBy}
                >
                    {busy && (
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                            aria-hidden="true"
                        />
                    )}
                    {confirmLabel}
                    {busy && (
                        <span className="visually-hidden"> in progress</span>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

/**
 * A recorded reason as the program's coordinators read it: the words, the
 * details, who gave it and when. Built from the four fields the server sends
 * a coordinator and no others. Faculty modes never mount it.
 */
export function EndReasonSummary({ endReason }) {
    if (!endReason?.code) return null;
    const { code, text, byRole, at } = endReason;
    return (
        <>
            <p className="fw-semibold mb-1">
                {endReasonWords(code, sideOfRole(byRole))}
            </p>
            {text && <Paragraphs value={text} />}
            <p className="small text-body-secondary mb-0">
                {`Given by ${givenByWords(byRole)}${at ? ` on ${formatDate(at)}` : ''}.`}
            </p>
        </>
    );
}
